/**
 * @file
 *
 * The base component that is used for initializing all the routes, context providers, etc.
 */
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { LocalizationProvider, AdapterDayjs } from 'lib/muiDatePickers';
import { ConfirmationDialogProvider } from './components/ConfirmationDialogProvider';
import React, { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from 'react-query';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';

import { GlobalErrorFallback } from './components/GlobalErrorFallback';
import { PageLoader } from './components/PageLoader';
import TabIndexedStorageContextProvider from './components/TabIndexedStorageContext';
import { queryClient } from './queryClient';
import { theme } from './theme';
import GlobalInactivityDialog from './components/GlobalInactivityDialog';
import { useSetLoginMethod } from './hooks/useSetLoginMethod';

const Connection = lazy(() =>
  import('./pages/Connection').then((m) => ({ default: m.Connection }))
);
const ConnectionsListing = lazy(() =>
  import('./pages/ConnectionsListing').then((m) => ({ default: m.ConnectionsListing }))
);
const Error = lazy(() => import('./pages/Error').then((m) => ({ default: m.Error })));
const Home = lazy(() => import('./pages/Home').then((m) => ({ default: m.Home })));

export default function App() {
  useSetLoginMethod();

  return (
    <TabIndexedStorageContextProvider>
      <Router>
        <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
          <QueryClientProvider client={queryClient} contextSharing>
            <ThemeProvider theme={theme}>
              <GlobalStyles
                styles={(muiTheme) => ({
                  html: {
                    height: `calc(100vh - ${muiTheme.spacing(30)})`,
                    width: '100%',
                  },
                  body: {
                    margin: 0,
                    width: '100%',
                    overflowX: 'hidden',
                    fontWeight: muiTheme.typography.fontWeightRegular,
                  },
                  '#root': {
                    width: '100%',
                    height: '100%',
                  },
                  a: {
                    textDecoration: 'inherit',
                    color: 'inherit',
                  },
                  '.wb-header': {
                    backgroundColor: '#354A5F',
                  },
                })}
              />
              <GlobalInactivityDialog />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Toaster position="bottom-left" toastOptions={{ duration: 4000 }} reverseOrder />
                <ConfirmationDialogProvider>
                  <CssBaseline />
                  <Suspense fallback={<PageLoader />}>
                    <Switch>
                      <Route path="/t/:tenantId/connection/:connectionId">
                        <Connection />
                      </Route>
                      <Route path="/t/:tenantId">
                        <ConnectionsListing />
                      </Route>
                      <Route exact path="/">
                        <Home />
                      </Route>
                      <Route path="*">
                        <Error />
                      </Route>
                    </Switch>
                  </Suspense>
                </ConfirmationDialogProvider>
              </LocalizationProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </Router>
    </TabIndexedStorageContextProvider>
  );
}

// TODO: use this for dark theme
// function ThemeWrapper({ children }) {
//   // const { theme } = useContext(ThemeSwitcherContext);

//   return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
// }
